import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { LimitExceededDetails } from '@/types/billing';
import { setLimitExceededHandler, clearLimitExceededHandler } from '@/api/client';
import { UpgradeRequiredModal } from './UpgradeRequiredModal';

interface LimitExceededContextValue {
  /** Currently displayed limit error, if any */
  currentLimit: LimitExceededDetails | null;
  /** Show the upgrade modal for a specific limit */
  showLimitError: (details: LimitExceededDetails) => void;
  /** Clear/hide the current limit error */
  clearLimitError: () => void;
}

const LimitExceededContext = createContext<LimitExceededContextValue | null>(null);

interface LimitExceededProviderProps {
  children: ReactNode;
}

/**
 * Provider that handles plan limit exceeded errors globally.
 * Automatically shows upgrade modal when 402 errors are received.
 */
export function LimitExceededProvider({ children }: LimitExceededProviderProps) {
  const [currentLimit, setCurrentLimit] = useState<LimitExceededDetails | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const showLimitError = useCallback((details: LimitExceededDetails) => {
    setCurrentLimit(details);
    setIsModalOpen(true);
  }, []);

  const clearLimitError = useCallback(() => {
    setCurrentLimit(null);
    setIsModalOpen(false);
  }, []);

  // Register global handler on mount
  useEffect(() => {
    setLimitExceededHandler(showLimitError);
    return () => {
      clearLimitExceededHandler();
    };
  }, [showLimitError]);

  const handleModalClose = (open: boolean) => {
    if (!open) {
      clearLimitError();
    }
  };

  return (
    <LimitExceededContext.Provider value={{ currentLimit, showLimitError, clearLimitError }}>
      {children}
      <UpgradeRequiredModal
        open={isModalOpen}
        onOpenChange={handleModalClose}
        limitDetails={currentLimit ?? undefined}
      />
    </LimitExceededContext.Provider>
  );
}

/**
 * Hook to access limit exceeded context
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useLimitExceeded() {
  const context = useContext(LimitExceededContext);
  if (!context) {
    throw new Error('useLimitExceeded must be used within a LimitExceededProvider');
  }
  return context;
}
